import { useCallback, useMemo, useState } from "react";
import { Campaign, DMAutomation } from "@markit/common.types";
import {
  GetDateWithTime,
  isDefaultTemplateAutomations,
  isPresetNoRecipientsAutomations,
} from "@markit/common.utils";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import ButtonWithDropdown from "../../Buttons/ButtonWithDropdown";
import { DropdownMenuItem } from "../../DropdownMenu";
import { useNavigate } from "react-router-dom";
import CampaignTypeItem from "../../CreatorDashboard/Campaigns/Items/CampaignTypeItem";
import CampaignRenameModal from "../../CreatorDashboard/Campaigns/Items/CampaignRenameModal";
import { CampaignDetails } from "../../../redux/slices/campaignSlice";
import CampaignStatusLabel from "../../CreatorDashboard/Campaigns/Items/CampaignStatusLabel";
import { Icon } from "@iconify/react";
import RectangleButton from "../../Buttons/RectangleButton";
import { onCampaignNavigatePath } from "../../../utils/navigationUtils";
import { AutomationItemTimeline } from "./AutomationItemTimeline";
import { NavigationId } from "../../../navigation/AppParamList";

export const AUTOMATION_ITEM_FLEX_SECTIONS = [2.5, 1, 1, 7];
export const AUTOMATION_ITEM_NO_RECIPIENTS_FLEX_SECTIONS = [2.5, 1, 8];

type AutomationItemPreviewProps = {
  campaignItem: Campaign;
  campaignDetail: CampaignDetails | undefined;
  onEventDashboard?: boolean;
  setShowDeleteCampaign?: (showDeleteCampaign: Campaign) => void;
  dmAutomation?: DMAutomation;
};

export const AutomationItemPreview = ({
  campaignItem,
  campaignDetail,
  onEventDashboard,
  setShowDeleteCampaign,
  dmAutomation,
}: AutomationItemPreviewProps) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);

  const styles = {
    itemBorder: { border: `1px solid ${theme.SubText.color}` },
  };

  const massTexts = useMemo(
    () =>
      campaignDetail
        ? campaignDetail.massTextDetails.map((detail) => detail.massText)
        : [],
    [campaignDetail]
  );

  const initialMassTextDetail = useMemo(
    () =>
      campaignDetail?.massTextDetails.find(
        (text) => text.massText.id === campaignItem.initialTextId
      ),
    [campaignDetail?.massTextDetails, campaignItem.initialTextId]
  );

  const showNoRecipientsSection = useMemo(
    () =>
      isPresetNoRecipientsAutomations(campaignItem.id) ||
      isDefaultTemplateAutomations(campaignItem.id),
    [campaignItem.id]
  );

  const flexSections = useMemo(
    () =>
      showNoRecipientsSection
        ? AUTOMATION_ITEM_NO_RECIPIENTS_FLEX_SECTIONS
        : AUTOMATION_ITEM_FLEX_SECTIONS,
    [showNoRecipientsSection]
  );

  const navigateCampaignItem = useCallback(
    () =>
      navigate(`/c/${campaignItem.id}`, {
        state: {
          campaignId: campaignItem.id,
          navigationPath: onEventDashboard
            ? undefined
            : NavigationId.HOME_TEXTING,
        },
      }),
    [campaignItem.id, navigate, onEventDashboard]
  );

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "View",
        icon: "ion:arrow-forward",
        key: "view",
        onPress: navigateCampaignItem,
      },
    ];
    if (campaignItem.eventId && setShowDeleteCampaign) {
      items.push(
        {
          title: "Rename",
          icon: "ion:pencil",
          key: "rename",
          onPress: () => {
            setShowRenameModal(true);
            setShowDropdown(false);
          },
        },
        {
          title: "Delete",
          icon: "ion:trash",
          key: "delete",
          onPress: () => {
            setShowDeleteCampaign(campaignItem);
            setShowDropdown(false);
          },
          isDestructive: true,
        }
      );
    }
    return items;
  }, [campaignItem, navigateCampaignItem, setShowDeleteCampaign]);

  if (!campaignDetail) {
    return null;
  }

  return (
    <>
      <div className="AlignedRow" style={{ height: 64 }}>
        <div
          className="ColumnNormalSelect"
          onClick={navigateCampaignItem}
          style={{ flex: flexSections[0], gap: 7 }}
        >
          <span className="AboutSubtitle OneLineText" style={{ fontSize: 14 }}>
            {campaignItem.title}
          </span>
          <CampaignTypeItem numMassTexts={massTexts.length} automation />
          <span className="smallBodySubtext">
            Last Updated: {GetDateWithTime(campaignItem.updatedAt, true)}
          </span>
        </div>
        <div style={{ flex: flexSections[1] }}>
          <CampaignStatusLabel
            campaign={campaignItem}
            massTexts={massTexts}
            initialMassTextDetail={initialMassTextDetail}
            dmAutomation={dmAutomation}
          />
        </div>
        {!showNoRecipientsSection ? (
          <div
            className="ColumnNormal"
            style={{ flex: flexSections[2], gap: 4 }}
          >
            <div style={{ paddingLeft: 14 }}>
              <div className="AlignedRow" style={{ gap: 4 }}>
                <Icon icon={"ion:person"} height={14} />
                <span style={{ fontSize: 14, color: theme.PrimaryText.color }}>
                  {campaignDetail.numRecipients}
                </span>
              </div>
              <span className="smallBodySubtext">Started</span>
            </div>
          </div>
        ) : null}
        <div
          className="AlignedRowSpaced"
          style={{
            flex: flexSections[showNoRecipientsSection ? 2 : 3],
            gap: 24,
            paddingLeft: showNoRecipientsSection ? 14 : 0,
          }}
        >
          <AutomationItemTimeline
            campaignItem={campaignItem}
            campaignDetail={campaignDetail}
            dmAutomationPlatform={dmAutomation?.platform}
          />
          {onEventDashboard ? (
            <div>
              <RectangleButton
                buttonLabel="Manage Texts"
                onPress={() =>
                  navigate(onCampaignNavigatePath(campaignItem.id), {
                    state: { campaignId: campaignItem.id },
                  })
                }
                altBorderRadius={100}
                altPaddingHorz={14}
                altPaddingVert={10}
                containerStyles={{
                  border: `1px solid ${Colors.GRAY2}`,
                  whiteSpace: "nowrap",
                }}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                iconRight={
                  <Icon
                    icon={"ion:arrow-forward"}
                    height={16}
                    style={{ transform: "rotateY(0deg) rotate(-45deg)" }}
                  />
                }
              />
            </div>
          ) : (
            <ButtonWithDropdown
              buttonLabel="View"
              textColor={Colors.BLACK}
              isOpenDropdown={showDropdown}
              setIsOpenDropdown={setShowDropdown}
              onPress={navigateCampaignItem}
              dropdownItems={dropdownItems}
              containerStyles={styles.itemBorder}
            />
          )}
        </div>
      </div>
      {showRenameModal ? (
        <CampaignRenameModal
          campaign={campaignItem}
          showModal={showRenameModal}
          setShowModal={setShowRenameModal}
        />
      ) : null}
    </>
  );
};
